import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, TrendingUp, TrendingDown, Award, AlertTriangle, BarChart3, Activity, Trash2, Edit3, Check, X, Brain, Lightbulb, Bot, Shield, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { listClosedPositions, deleteClosedPosition, updateClosedPosition, getClosedTradesStats, getClosedTradesEdgeBuckets, getExpertSuggestions, acceptExpertSuggestion, dismissExpertSuggestion, triggerLearningCheck, adminGetExpertClosedTrades, adminGetExpertStats, adminGetExpertEdgeBuckets, listExclusionRules, toggleExclusionRule, deleteExclusionRule } from "../lib/api";
import { useAuth } from "../lib/auth";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));
const pctColor = (v) => v == null ? "var(--text-mute)" : v > 0 ? "#22c55e" : v < 0 ? "#ef4444" : "var(--text-mute)";
const fmtDuration = (sec) => {
    if (!sec || sec < 0) return "—";
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
    return `${Math.round(sec / 86400)}g`;
};
const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso || "—"; }
};

export default function ClosedPositions() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [scope, setScope] = useState("mine"); // "mine" | "expert" (admin only)
    const [tab, setTab] = useState("history");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // Master Prompt — Snapshot all'apertura del trade (Modifica 4):
    // statistiche sui soli snapshot immutabili, indipendenti dalla dashboard
    // Edge esistente (che resta invariata).
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    // Nuova vista IL MIO EDGE per bucket (richiesta utente 12 feb '26):
    // tabelle divise per parametro tecnico (BIAS, MTF, Trader/Volume Score,
    // Validazione Breakout, RS, Momentum, Stop Hunt) con conteggio
    // vincenti/perdenti basato su snapshot_at_open.
    const [edgeBuckets, setEdgeBuckets] = useState(null);
    const [edgeBucketsLoading, setEdgeBucketsLoading] = useState(true);
    // Part F — expert layer learning suggestions (self-correction proposals)
    const [suggestions, setSuggestions] = useState([]);
    // Section 3 Part B — active exclusion rules (admin can toggle/delete)
    const [rules, setRules] = useState([]);
    const [rulesMeta, setRulesMeta] = useState({ review_days: 90, stale_count: 0 });

    const reloadSuggestions = async () => {
        try {
            const r = await getExpertSuggestions();
            setSuggestions(r?.items || []);
        } catch {
            setSuggestions([]);
        }
    };

    const reloadRules = async () => {
        if (!isAdmin) return;
        try {
            const r = await listExclusionRules(false);
            setRules(r?.items || []);
            setRulesMeta({
                review_days: r?.review_days ?? 90,
                stale_count: r?.stale_count ?? 0,
            });
        } catch {
            setRules([]);
        }
    };

    const reload = async () => {
        setLoading(true);
        try {
            // Admin can flip to a global expert_auto view; regular users only
            // see their own. Same shape either way.
            const useExpert = isAdmin && scope === "expert";
            const a = useExpert ? await adminGetExpertClosedTrades() : await listClosedPositions();
            setItems(a.items || []);
        } catch (e) {
            toast.error("Errore caricamento posizioni chiuse");
        } finally {
            setLoading(false);
        }
    };

    const reloadStats = async () => {
        setStatsLoading(true);
        try {
            // Scope-EDGE fix (Feb 2026): stats endpoint follows the scope
            // toggle — expert scope uses admin/expert-trades/stats which
            // filters by opened_by=expert_auto globally.
            const useExpert = isAdmin && scope === "expert";
            const s = useExpert ? await adminGetExpertStats() : await getClosedTradesStats();
            setStats(s || null);
        } catch {
            setStats({ has_data: false, message: "Errore caricamento statistiche snapshot", patterns: [] });
        } finally {
            setStatsLoading(false);
        }
    };

    const reloadEdgeBuckets = async () => {
        setEdgeBucketsLoading(true);
        try {
            const useExpert = isAdmin && scope === "expert";
            const b = useExpert ? await adminGetExpertEdgeBuckets() : await getClosedTradesEdgeBuckets();
            setEdgeBuckets(b || null);
        } catch {
            setEdgeBuckets({ has_data: false, message: "Errore caricamento bucket Edge", groups: [] });
        } finally {
            setEdgeBucketsLoading(false);
        }
    };

    useEffect(() => { reload(); reloadStats(); reloadEdgeBuckets(); }, [scope]);
    useEffect(() => { reloadSuggestions(); reloadRules(); }, []);
    useEffect(() => { reloadRules(); }, [isAdmin]);
    // Scope-EDGE fix — IL MIO EDGE tab NOW exists in expert scope too
    // (fed by /admin/expert-trades/edge-buckets), so we no longer force
    // tab=history on scope change.

    return (
        <div className="container mx-auto px-3 py-6 max-w-7xl" data-testid="closed-positions-page">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Archive size={20} style={{ color: "var(--brand-blue)" }} />
                    <h1 className="text-xl font-bold tracking-wide">
                        {scope === "expert" ? "TRADE EXPERT AUTO — STORICO" : "POSIZIONI CHIUSE"}
                    </h1>
                    {items.length > 0 && (
                        <span className="text-[10px] text-mute tracking-widest ml-2">
                            {items.length} TRADE STORICI
                        </span>
                    )}
                </div>
                {isAdmin && (
                    <div
                        className="inline-flex border border-soft"
                        data-testid="expert-scope-toggle"
                        role="tablist"
                        aria-label="Ambito visualizzazione trade chiusi"
                    >
                        <button
                            type="button"
                            data-testid="scope-mine"
                            onClick={() => setScope("mine")}
                            aria-selected={scope === "mine"}
                            className="px-3 py-2 text-[10px] font-bold tracking-wider transition-colors"
                            style={scope === "mine"
                                ? { color: "#000", background: "var(--amber)", borderRight: "1px solid var(--amber)" }
                                : { color: "var(--text-mute)", borderRight: "1px solid var(--border-soft)" }}
                        >
                            I MIEI TRADE
                        </button>
                        <button
                            type="button"
                            data-testid="scope-expert"
                            onClick={() => setScope("expert")}
                            aria-selected={scope === "expert"}
                            className="px-3 py-2 text-[10px] font-bold tracking-wider transition-colors flex items-center gap-1.5"
                            style={scope === "expert"
                                ? { color: "#000", background: "#a855f7" }
                                : { color: "var(--text-mute)" }}
                        >
                            <Bot size={11} />
                            TRADE EXPERT AUTO
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs — the "IL MIO EDGE" tab is per-user analytics, hidden in expert scope */}
            <div className="flex gap-1 mb-4 border-b border-soft">
                <button
                    data-testid="tab-history"
                    onClick={() => setTab("history")}
                    className={`px-3 py-2 text-xs tracking-widest font-bold border-b-2 transition-colors ${tab === "history" ? "border-amber-400 text-amber-400" : "border-transparent text-mute hover:text-dim"}`}>
                    <Activity size={12} className="inline mr-1.5" />STORICO
                </button>
                {/* Scope-EDGE fix — IL MIO EDGE is visible in BOTH scopes now.
                    In expert scope it's fed by /admin/expert-trades/edge-buckets
                    so the buckets reflect only opened_by=expert_auto data. */}
                <button
                    data-testid="tab-edge"
                    onClick={() => setTab("edge")}
                    className={`px-3 py-2 text-xs tracking-widest font-bold border-b-2 transition-colors ${tab === "edge" ? "border-amber-400 text-amber-400" : "border-transparent text-mute hover:text-dim"}`}>
                    <BarChart3 size={12} className="inline mr-1.5" />
                    {scope === "expert" ? "EDGE EXPERT AUTO" : "IL MIO EDGE"}
                </button>
            </div>

            {loading ? (
                <div className="text-center text-mute py-12">Caricamento…</div>
            ) : tab === "history" ? (
                <HistoryView
                    items={items}
                    onReload={reload}
                    stats={stats}
                    statsLoading={statsLoading}
                    readOnly={scope === "expert"}
                />
            ) : (
                <EdgeView
                    buckets={edgeBuckets}
                    loading={edgeBucketsLoading}
                    onReload={reloadEdgeBuckets}
                    suggestions={suggestions}
                    onSuggestionsReload={reloadSuggestions}
                    rules={rules}
                    rulesMeta={rulesMeta}
                    onRulesReload={reloadRules}
                    isAdmin={isAdmin}
                    scope={scope}
                />
            )}
        </div>
    );
}

/* ---------------- History Tab ---------------- */
function HistoryView({ items, onReload, stats, statsLoading, readOnly = false }) {
    if (items.length === 0) {
        return (
            <div className="border border-soft p-8 text-center" data-testid="empty-history">
                <Archive size={32} className="mx-auto mb-3 text-mute" />
                <div className="text-sm text-dim mb-1">
                    {readOnly ? "Nessun trade Expert Auto ancora chiuso" : "Nessuna posizione chiusa"}
                </div>
                <div className="text-xs text-mute">
                    {readOnly
                        ? "Il layer autonomo non ha ancora chiuso alcun trade — le uscite del bot compariranno qui non appena Stop Loss o Take Profit scatteranno."
                        : "Le posizioni chiuse manualmente o auto-chiuse da SL/TP appariranno qui con snapshot dello stato iniziale."}
                </div>
            </div>
        );
    }
    const handleDelete = async (id, sym) => {
        if (!window.confirm(`Eliminare la posizione chiusa ${sym} dallo storico?`)) return;
        try {
            await deleteClosedPosition(id);
            toast.success("Posizione eliminata dallo storico");
            onReload();
        } catch { toast.error("Errore eliminazione"); }
    };
    return (
        <div className="space-y-2" data-testid="history-list">
            {!readOnly && <SnapshotStatsHeader stats={stats} loading={statsLoading} />}
            {items.map(p => (
                <ClosedPositionCard
                    key={p.id}
                    position={p}
                    onDelete={() => handleDelete(p.id, p.symbol)}
                    onUpdated={() => onReload()}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
}

/* ---------------- Snapshot stats header (Master Prompt Modifica 4) ---------------- */
function SnapshotStatsHeader({ stats, loading }) {
    if (loading) {
        return (
            <div data-testid="snapshot-stats-loading" style={{ fontSize: "0.7rem", color: "#6b7280", padding: "6px 0 10px" }}>
                Caricamento statistiche snapshot…
            </div>
        );
    }
    if (!stats) return null;

    // 4D — dati insufficienti
    if (!stats.has_data) {
        return (
            <div
                data-testid="snapshot-stats-empty"
                style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    padding: "8px 0 12px 0",
                    borderBottom: "1px solid var(--soft)",
                    marginBottom: "12px",
                }}>
                {stats.message}
            </div>
        );
    }

    return (
        <>
            {/* 4B — header riepilogo */}
            <div
                data-testid="snapshot-stats-header"
                style={{
                    display: "flex",
                    gap: 16,
                    padding: "8px 0",
                    borderBottom: "1px solid var(--soft)",
                    marginBottom: 12,
                    fontSize: "0.75rem",
                    flexWrap: "wrap",
                }}>
                <span style={{ color: "#9ca3af" }}>Analizzati: {stats.total_trades}</span>
                <span style={{ color: "#22c55e" }}>✓ Vincenti: {stats.winners_count}</span>
                <span style={{ color: "#ef4444" }}>✗ Perdenti: {stats.losers_count}</span>
                <span style={{ color: "#6b7280" }}>Win Rate: {stats.win_rate}%</span>
            </div>

            {/* 4C — pattern rilevati */}
            {Array.isArray(stats.patterns) && stats.patterns.length > 0 && (
                <div data-testid="snapshot-patterns" style={{ marginBottom: 16 }}>
                    <div style={{
                        fontSize: "0.7rem",
                        color: "#6b7280",
                        letterSpacing: "0.1em",
                        marginBottom: 8,
                    }}>
                        PATTERN RILEVATI
                    </div>
                    {stats.patterns.map((pattern, idx) => {
                        const sigColor = pattern.significance === "ALTA" ? "#22c55e"
                            : pattern.significance === "MEDIA" ? "#eab308"
                            : "#6b7280";
                        return (
                            <div
                                key={idx}
                                data-testid={`snapshot-pattern-${idx}`}
                                style={{
                                    border: "1px solid var(--soft)",
                                    borderRadius: 4,
                                    padding: "8px 12px",
                                    marginBottom: 6,
                                    background: "var(--elev)",
                                }}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 4,
                                }}>
                                    <span style={{
                                        fontSize: "0.65rem",
                                        color: sigColor,
                                        border: "1px solid currentColor",
                                        padding: "1px 4px",
                                        borderRadius: 2,
                                    }}>
                                        {pattern.significance}
                                    </span>
                                    <span style={{ fontSize: "0.8rem", color: "#e5e7eb" }}>
                                        {pattern.label}
                                    </span>
                                </div>
                                <div style={{ display: "flex", gap: 16, fontSize: "0.75rem" }}>
                                    <span style={{ color: "#22c55e" }}>
                                        Vincenti: {pattern.winners_value}{pattern.suffix}
                                    </span>
                                    <span style={{ color: "#ef4444" }}>
                                        Perdenti: {pattern.losers_value}{pattern.suffix}
                                    </span>
                                </div>
                                <div style={{ fontSize: "0.7rem", color: "#6b7280", marginTop: 2 }}>
                                    {pattern.insight}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}

/* ---------------- Single closed position card with inline edit ---------------- */
function ClosedPositionCard({ position: p, onDelete, onUpdated, readOnly = false }) {
    const [editing, setEditing] = useState(false);
    const [exitDraft, setExitDraft] = useState(p.exit_price ?? "");
    const [reasonDraft, setReasonDraft] = useState(p.exit_reason ?? "manual");
    const [pnlDraft, setPnlDraft] = useState(p.pnl_pct ?? "");
    const [saving, setSaving] = useState(false);
    // Master Prompt — Snapshot all'apertura del trade (Modifica 5)
    const [expandedSnap, setExpandedSnap] = useState({});
    const toggleSnap = (key) => setExpandedSnap(prev => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => { setExitDraft(p.exit_price ?? ""); }, [p.exit_price]);
    useEffect(() => { setReasonDraft(p.exit_reason ?? "manual"); }, [p.exit_reason]);
    useEffect(() => { setPnlDraft(p.pnl_pct ?? ""); }, [p.pnl_pct]);

    const isLong = (p.side || "").toLowerCase() === "long";
    const Icon = isLong ? TrendingUp : TrendingDown;
    const snap = p.initial_snapshot || {};
    // Modifica 5 — snapshot esteso e immutabile creato all'apertura del trade
    const snapOpen = p.snapshot_at_open || null;
    const tradeKey = String(p._id || p.id);
    const reasonLabel = p.exit_reason === "tp" ? "🎯 Take Profit"
                      : p.exit_reason === "sl" ? "🛑 Stop Loss"
                      : "👋 Manuale";

    const handleSave = async () => {
        setSaving(true);
        try {
            // Build payload: exit_price wins. If user typed PnL directly,
            // we'll send only PnL (no exit_price).
            const rawExit = String(exitDraft).trim().replace(",", ".");
            const rawPnl = String(pnlDraft).trim().replace(",", ".");
            const payload = { exit_reason: reasonDraft };
            const newExit = rawExit === "" ? null : parseFloat(rawExit);
            const newPnl = rawPnl === "" ? null : parseFloat(rawPnl);
            // If exit_price changed, use it (recomputes pnl_pct/abs server-side)
            if (newExit != null && !Number.isNaN(newExit) && Math.abs(newExit - (p.exit_price ?? -1)) > 1e-9) {
                payload.exit_price = newExit;
            } else if (newPnl != null && !Number.isNaN(newPnl) && Math.abs(newPnl - (p.pnl_pct ?? -1)) > 1e-9) {
                // User typed PnL% directly without changing exit_price
                payload.pnl_pct = newPnl;
            }
            await updateClosedPosition(p.id, payload);
            toast.success("Posizione aggiornata");
            setEditing(false);
            onUpdated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore aggiornamento");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid={`closed-card-${p.symbol}`}
            className="border border-soft bg-elev p-3 grid grid-cols-12 gap-3 items-center hover:border-amber-400/30 transition-colors">
            {/* Symbol + side */}
            <div className="col-span-2 flex items-center gap-2 min-w-0">
                <Icon size={14} style={{ color: isLong ? "#22c55e" : "#ef4444" }} />
                <Link
                    to={`/asset/${encodeURIComponent(p.symbol)}`}
                    className="font-bold text-sm hover:underline truncate"
                    style={{ color: "var(--brand-blue)" }}>{p.symbol}</Link>
                <span className="text-[9px] uppercase text-mute">{p.side}</span>
                {p.manually_edited && (
                    <span title="Posizione modificata manualmente" className="text-[8px] px-1 border border-soft text-amber-400">
                        EDIT
                    </span>
                )}
                {p.data_quality_flag === "stale_exit_price" && (
                    <span
                        data-testid={`stale-exit-flag-${p.symbol}`}
                        title="Chiuso via SL/TP con prezzo di uscita potenzialmente stantio (fino a ~2%). Il segno win/loss resta valido, la grandezza del P/L no."
                        className="text-[8px] px-1 border"
                        style={{ borderColor: "#f97316", color: "#f97316" }}>
                        PREZZO STANTIO
                    </span>
                )}
                {p.data_quality_flag === "suspected_stale_duplicate" && (
                    <span
                        data-testid={`stale-dup-flag-${p.symbol}`}
                        title="Trade chiuso in meno di 60 secondi — probabile artefatto tecnico (dati stantii). Escluso dai calcoli."
                        className="text-[8px] px-1 border"
                        style={{ borderColor: "#ef4444", color: "#ef4444" }}>
                        DUPLICATO SOSPETTO
                    </span>
                )}
            </div>
            {/* PnL */}
            <div className="col-span-2 text-right tnum">
                <div className="text-xs text-mute">PnL</div>
                {editing ? (
                    <input
                        type="number" step="any" value={pnlDraft}
                        onChange={(e) => setPnlDraft(e.target.value)}
                        data-testid={`edit-pnl-${p.symbol}`}
                        placeholder="% es. 5.5"
                        className="w-full bg-elev2 border border-soft px-1 py-0.5 text-xs tnum text-right" />
                ) : (
                    <>
                        <div className="font-bold text-sm" style={{ color: pctColor(p.pnl_pct) }}>
                            {p.pnl_pct != null ? `${p.pnl_pct > 0 ? "+" : ""}${p.pnl_pct.toFixed(2)}%` : "—"}
                        </div>
                        <div className="text-[10px] text-dim">${fmt(p.pnl_abs)}</div>
                    </>
                )}
            </div>
            {/* Entry → Exit */}
            <div className="col-span-2 tnum">
                <div className="text-[10px] text-mute">Entry → Exit</div>
                <div className="text-xs flex items-center gap-1">
                    ${fmt(p.entry_price)} →
                    {editing ? (
                        <input
                            type="number" step="any" value={exitDraft}
                            onChange={(e) => setExitDraft(e.target.value)}
                            data-testid={`edit-exit-${p.symbol}`}
                            className="flex-1 min-w-0 bg-elev2 border border-soft px-1 py-0.5 text-xs tnum" />
                    ) : (
                        <span className="font-bold">${fmt(p.exit_price)}</span>
                    )}
                </div>
            </div>
            {/* Reason */}
            <div className="col-span-2 text-[10px]">
                <div className="text-mute">Chiusura</div>
                {editing ? (
                    <select
                        value={reasonDraft}
                        onChange={(e) => setReasonDraft(e.target.value)}
                        data-testid={`edit-reason-${p.symbol}`}
                        className="w-full bg-elev2 border border-soft px-1 py-0.5 text-xs">
                        <option value="manual">👋 Manuale</option>
                        <option value="tp">🎯 Take Profit</option>
                        <option value="sl">🛑 Stop Loss</option>
                    </select>
                ) : (
                    <div className="text-dim font-bold">{reasonLabel}</div>
                )}
            </div>
            {/* Initial snapshot scores */}
            <div className="col-span-2 flex flex-wrap gap-1">
                {snap.trader_score != null && <Pill label="T" value={snap.trader_score} hint="Trader Score iniziale" />}
                {snap.ai_confidence_score != null && <Pill label="AI" value={snap.ai_confidence_score} hint="AI Confidence iniziale" />}
                {snap.opportunity_quality_score != null && <Pill label="OQS" value={snap.opportunity_quality_score} hint="OQS iniziale" />}
                {snap.initial_rr_t2 != null && (
                    <span className="text-[9px] tnum font-bold px-1.5 py-0.5 border border-soft text-dim">R/R {snap.initial_rr_t2.toFixed(2)}</span>
                )}
            </div>
            {/* Duration + edit + delete actions */}
            <div className="col-span-2 flex items-center justify-end gap-2">
                <div className="text-right">
                    <div className="text-[10px] text-mute">Durata</div>
                    <div className="text-xs font-bold">{fmtDuration(p.duration_seconds)}</div>
                </div>
                {readOnly ? (
                    <span className="text-[9px] tracking-widest px-1.5 py-0.5 border flex items-center gap-1" style={{ color: "#a855f7", borderColor: "#a855f7" }} title="Trade autonomo — sola lettura">
                        <Bot size={9} />BOT
                    </span>
                ) : editing ? (
                    <>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid={`save-edit-${p.symbol}`}
                            className="text-green-500 hover:text-green-400 disabled:opacity-50">
                            <Check size={14} />
                        </button>
                        <button
                            onClick={() => { setEditing(false); setExitDraft(p.exit_price ?? ""); setPnlDraft(p.pnl_pct ?? ""); }}
                            className="text-mute hover:text-dim">
                            <X size={14} />
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => setEditing(true)}
                            data-testid={`edit-closed-${p.symbol}`}
                            className="text-mute hover:text-amber-400"
                            title="Modifica esito (exit price / PnL / motivo)">
                            <Edit3 size={12} />
                        </button>
                        <button
                            onClick={onDelete}
                            data-testid={`delete-closed-${p.symbol}`}
                            className="text-mute hover:text-red-400"
                            title="Elimina dallo storico">
                            <Trash2 size={12} />
                        </button>
                    </>
                )}
            </div>
            {/* Footer dates */}
            <div className="col-span-12 flex items-center justify-between text-[9px] text-mute pt-1 border-t border-soft/50 mt-1">
                <span>Aperto {fmtDate(p.opened_at)}</span>
                <span>{snap.market_regime || "—"}</span>
                <span>Chiuso {fmtDate(p.closed_at)}{p.manually_edited ? " · modificato" : ""}</span>
            </div>
            {editing && (
                <div className="col-span-12 text-[10px] text-mute italic border-t border-soft/30 pt-2 mt-1">
                    💡 Modifica il <strong>prezzo di uscita</strong> per ricalcolare automaticamente il PnL, oppure inserisci direttamente il <strong>PnL %</strong> per overridarlo.
                </div>
            )}
            {/* Modifica 5 — DATI APERTURA (sezione espandibile) */}
            {snapOpen && (
                <div className="col-span-12">
                    <div
                        data-testid={`snap-toggle-${p.symbol}`}
                        onClick={() => toggleSnap(tradeKey)}
                        style={{
                            fontSize: "0.72rem",
                            color: "#6b7280",
                            cursor: "pointer",
                            padding: "4px 0",
                            borderTop: "1px solid var(--soft)",
                            marginTop: 4,
                            userSelect: "none",
                        }}>
                        DATI APERTURA {expandedSnap[tradeKey] ? "▲" : "▼"}
                    </div>
                    {expandedSnap[tradeKey] && (
                        <div
                            data-testid={`snap-body-${p.symbol}`}
                            style={{
                                fontSize: "0.72rem",
                                color: "#9ca3af",
                                padding: 8,
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "4px 16px",
                            }}>
                            {snapOpen.snapshot_source != null && (
                                <span style={{ gridColumn: "1/-1", fontSize: "0.65rem", color: "#6b7280", marginBottom: 4 }}>
                                    {snapOpen.snapshot_source === "order_placement"
                                        ? "📋 Dati al momento dell'ordine"
                                        : "📍 Dati al momento dell'apertura"}
                                </span>
                            )}
                            {snapOpen.trader_score != null && (
                                <span>Score: {snapOpen.trader_score}/100</span>
                            )}
                            {snapOpen.market_regime != null && (
                                <span>Regime: {snapOpen.market_regime}</span>
                            )}
                            {snapOpen.final_bias != null && (
                                <span>Bias: {snapOpen.final_bias}</span>
                            )}
                            {snapOpen.mtf_score != null && (
                                <span>MTF: {snapOpen.mtf_score}/100</span>
                            )}
                            {snapOpen.momentum_score != null && (
                                <span>
                                    Momentum: {snapOpen.momentum_score}/100
                                    {snapOpen.momentum_label ? ` (${snapOpen.momentum_label})` : ""}
                                </span>
                            )}
                            {snapOpen.relative_strength_score != null && (
                                <span>RS: {snapOpen.relative_strength_score}/100</span>
                            )}
                            {snapOpen.rr_t2 != null && (
                                <span>R/R T2: {snapOpen.rr_t2}x</span>
                            )}
                            {snapOpen.stop_hunt_detected != null && (
                                <span>Stop Hunt: {snapOpen.stop_hunt_detected ? "✓ Sì" : "✗ No"}</span>
                            )}
                            {snapOpen.structure_label != null && (
                                <span>Struttura: {snapOpen.structure_label}</span>
                            )}
                            {snapOpen.fvg_entry_available != null && (
                                <span>FVG: {snapOpen.fvg_entry_available ? "✓ Disponibile" : "✗ No"}</span>
                            )}
                            {snapOpen.ob_available != null && (
                                <span>OB: {snapOpen.ob_available ? "✓ Disponibile" : "✗ No"}</span>
                            )}
                            {Array.isArray(snapOpen.badges) && snapOpen.badges.length > 0 && (
                                <span style={{ gridColumn: "1/-1" }}>
                                    Badge: {snapOpen.badges.join(" · ")}
                                </span>
                            )}
                            {snapOpen.snapshot_at && (
                                <span style={{ gridColumn: "1/-1", color: "#4b5563", fontSize: "0.65rem", marginTop: 4 }}>
                                    Snapshot creato: {(() => {
                                        const diff = Date.now() - new Date(snapOpen.snapshot_at).getTime();
                                        const min = Math.floor(diff / 60000);
                                        const hrs = Math.floor(diff / 3600000);
                                        const days = Math.floor(diff / 86400000);
                                        if (min < 60) return `${min}min fa`;
                                        if (hrs < 24) return `${hrs}h fa`;
                                        return `${days}gg fa`;
                                    })()}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const Pill = ({ label, value, hint }) => (
    <span
        title={hint}
        className="inline-flex items-center gap-1 text-[9px] tnum font-bold px-1.5 py-0.5 border border-soft"
        style={{ color: value >= 80 ? "#22c55e" : value >= 60 ? "#f59e0b" : "var(--text-mute)" }}>
        <span className="text-mute">{label}</span>{Math.round(value)}
    </span>
);

/* ---------------- Edge Tab — bucket per parametro tecnico ---------------- */
/* Master Prompt v2 (12 feb '26): l'utente vuole vedere, per ciascun
   parametro tecnico (BIAS, MTF, Trader Score, Volume Score, Validazione
   Breakout, RS, Momentum, Stop Hunt), QUANTI dei suoi trade chiusi
   in profitto e in perdita avevano quel valore al momento dell'apertura.
   I dati arrivano da `snapshot_at_open` via GET /api/closed-trades/edge-buckets. */

function EdgeView({ buckets, loading, onReload, suggestions, onSuggestionsReload,
                     rules = [], rulesMeta = { review_days: 90, stale_count: 0 },
                     onRulesReload, isAdmin = false, scope = "mine" }) {
    // Scope-EDGE fix — Suggerimenti + regole di esclusione sono strumenti di
    // auto-correzione del BOT; hanno senso solo nello scope Expert Auto.
    // Nello scope "I miei trade" non li mostriamo.
    const showBotTools = scope === "expert" && isAdmin;
    const hasSuggestions = (suggestions || []).length > 0;
    const hasRules = (rules || []).length > 0;

    const handleToggleRule = async (id, active) => {
        try {
            await toggleExclusionRule(id, active);
            toast.success(active ? "Regola riattivata" : "Regola disattivata");
            onRulesReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore toggle regola");
        }
    };
    const handleDeleteRule = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa regola di esclusione?")) return;
        try {
            await deleteExclusionRule(id);
            toast.success("Regola eliminata");
            onRulesReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore eliminazione regola");
        }
    };

    const handleAccept = async (id) => {
        try {
            await acceptExpertSuggestion(id);
            toast.success("Suggestion accepted — config applied");
            onSuggestionsReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to accept suggestion");
        }
    };
    const handleDismiss = async (id) => {
        try {
            await dismissExpertSuggestion(id);
            toast.success("Suggestion dismissed");
            onSuggestionsReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to dismiss suggestion");
        }
    };
    const handleForceCheck = async () => {
        try {
            const r = await triggerLearningCheck();
            toast.success(`Learning check: ${r?.status || "ok"}`);
            onSuggestionsReload?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Learning check failed");
        }
    };
    if (loading && !buckets) {
        return (
            <div data-testid="edge-loading" className="text-center text-mute py-8 text-sm">
                Caricamento Edge analytics…
            </div>
        );
    }
    if (!buckets || !buckets.has_data) {
        return (
            <div className="space-y-4">
                {showBotTools && hasSuggestions && (
                    <div className="border border-soft bg-elev p-3" data-testid="expert-suggestions-panel-empty">
                        <div className="text-[11px] tracking-widest text-mute mb-2 flex items-center gap-1.5">
                            <Brain size={12} style={{ color: "var(--brand-blue)" }} />
                            AI TRADER — SUGGERIMENTI DI AUTO-CORREZIONE ({suggestions.length})
                        </div>
                        <div className="text-[10px] text-mute">
                            Suggerimenti disponibili anche prima di popolare l&apos;Edge dashboard —
                            passa in &quot;Storico&quot; per vedere i trade sottostanti.
                        </div>
                    </div>
                )}
                <div className="border border-soft p-8 text-center" data-testid="empty-edge">
                    <BarChart3 size={32} className="mx-auto mb-3 text-mute" />
                    <div className="text-sm text-dim mb-1">Edge analytics non ancora disponibile</div>
                    <div className="text-xs text-mute max-w-lg mx-auto">
                        {buckets?.message ||
                            "Nessun trade chiuso con snapshot all'apertura. I trade aperti da oggi includeranno automaticamente lo snapshot tecnico al momento dell'apertura (o del piazzamento dell'ordine)."}
                    </div>
                    <button
                        onClick={onReload}
                        data-testid="edge-reload"
                        className="mt-4 text-[10px] tracking-widest text-amber-400 hover:underline">
                        AGGIORNA
                    </button>
                </div>
            </div>
        );
    }

    const wr = buckets.win_rate ?? 0;
    return (
        <div className="space-y-4" data-testid="edge-bucket-dashboard">
            {/* Header riepilogo */}
            <div
                data-testid="edge-bucket-header"
                className="border border-soft bg-elev p-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] tracking-widest text-mute uppercase mb-1">
                        Dataset analizzato
                    </div>
                    <div className="text-xs text-dim">
                        Snapshot tecnico immutabile catturato all&apos;apertura di ciascun trade
                        (o al piazzamento dell&apos;ordine).
                    </div>
                </div>
                <div className="flex gap-4 text-xs tnum">
                    <span><span className="text-mute">Trade:</span> <strong>{buckets.total}</strong></span>
                    <span style={{ color: "#22c55e" }}>✓ {buckets.winners}</span>
                    <span style={{ color: "#ef4444" }}>✗ {buckets.losers}</span>
                    <span style={{ color: wr >= 50 ? "#22c55e" : "#ef4444" }}>
                        WR: <strong>{wr}%</strong>
                    </span>
                </div>
            </div>

            {/* Section 3 Part B — Exclusion rules panel (expert scope only) */}
            {showBotTools && (
                <div className="border border-soft bg-elev p-3" data-testid="exclusion-rules-panel">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 text-[11px] tracking-widest text-mute">
                            <Shield size={12} style={{ color: "#a855f7" }} />
                            REGOLE DI ESCLUSIONE
                            <span className="ml-1 text-[9px] text-dim">
                                {hasRules ? `${rules.length} regole (${rules.filter(r => r.active).length} attive)` : "nessuna regola definita"}
                            </span>
                        </div>
                        {rulesMeta.stale_count > 0 && (
                            <span
                                data-testid="stale-rules-warning"
                                className="text-[9px] tracking-widest px-1.5 py-0.5 border"
                                style={{ borderColor: "#f97316", color: "#f97316" }}
                                title={`${rulesMeta.stale_count} regola/e attiva da >${rulesMeta.review_days} giorni — rivalutare per non renderla infalsificabile`}>
                                {rulesMeta.stale_count} DA RIVALUTARE
                            </span>
                        )}
                    </div>
                    {hasRules ? (
                        <div className="space-y-1.5">
                            {rules.map(r => (
                                <div key={r.id}
                                    data-testid={`rule-${r.id}`}
                                    className="border border-soft bg-elev2 p-2 flex items-center gap-2 flex-wrap text-xs">
                                    <span className={`text-[9px] tracking-widest px-1 border ${r.active ? "" : "opacity-50"}`}
                                        style={{ borderColor: r.active ? "#22c55e" : "#6b7280",
                                                   color: r.active ? "#22c55e" : "#6b7280" }}>
                                        {r.active ? "ATTIVA" : "INATTIVA"}
                                    </span>
                                    <span className="tnum text-mute">
                                        {r.field} {r.operator} {typeof r.value === 'string' ? `"${r.value}"` : JSON.stringify(r.value)}
                                    </span>
                                    {r.direction && r.direction !== "both" && (
                                        <span className="text-[9px] text-mute uppercase">dir: {r.direction}</span>
                                    )}
                                    <span className="text-dim flex-1 min-w-0 truncate">{r.label}</span>
                                    {r.blocked_count > 0 && (
                                        <span className="text-[9px] text-mute" title="Aperture bloccate">
                                            🚫 {r.blocked_count}
                                        </span>
                                    )}
                                    <span className="text-[9px] text-mute" title="Età regola">
                                        {r.age_days != null ? `${r.age_days}d` : "?"}
                                    </span>
                                    {r.needs_review && (
                                        <span className="text-[9px]" style={{ color: "#f97316" }} title="Rivalutare">⚠</span>
                                    )}
                                    <button
                                        onClick={() => handleToggleRule(r.id, !r.active)}
                                        data-testid={`toggle-rule-${r.id}`}
                                        className="text-mute hover:text-amber-400"
                                        title={r.active ? "Disattiva regola" : "Riattiva regola"}>
                                        <RotateCcw size={12} />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteRule(r.id)}
                                        data-testid={`delete-rule-${r.id}`}
                                        className="text-mute hover:text-red-400"
                                        title="Elimina regola">
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-[10px] text-mute py-1">
                            Nessuna regola. Le regole vengono create accettando un suggerimento AI Trader qui sopra.
                        </div>
                    )}
                </div>
            )}

            {/* Part F — Learning suggestions panel (expert scope only) */}
            {showBotTools && (
            <div className="border border-soft bg-elev p-3" data-testid="expert-suggestions-panel">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[11px] tracking-widest text-mute">
                        <Brain size={12} style={{ color: "var(--brand-blue)" }} />
                        AI TRADER — SUGGERIMENTI DI AUTO-CORREZIONE
                        <span className="ml-1 text-[9px] text-dim">
                            {hasSuggestions ? `${suggestions.length} in attesa` : "nessun suggerimento pendente"}
                        </span>
                    </div>
                    <button
                        onClick={handleForceCheck}
                        data-testid="force-learning-check"
                        className="text-[9px] tracking-widest text-amber-400 hover:underline">
                        FORZA CHECK
                    </button>
                </div>
                {hasSuggestions ? (
                    <div className="space-y-2">
                        {suggestions.map((s) => (
                            <div key={s.id}
                                data-testid={`suggestion-${s.id}`}
                                className="border border-soft p-2 text-[11px]">
                                <div className="flex items-start gap-2 mb-1">
                                    <Lightbulb size={12} className="mt-0.5" style={{ color: "#f59e0b" }} />
                                    <div className="flex-1">
                                        <div className="text-dim">{s.text}</div>
                                        <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-mute">
                                            <span>
                                                <span className="uppercase tracking-wider">WR attuale:</span>{" "}
                                                <span className="text-dim tnum">{s.baseline_win_rate}%</span>{" "}
                                                <span className="text-mute">(su {s.baseline_sample_size} trade)</span>
                                            </span>
                                            <span>→</span>
                                            <span>
                                                <span className="uppercase tracking-wider">WR proiettato:</span>{" "}
                                                <span className="tnum" style={{ color: s.projected_win_rate > s.baseline_win_rate ? "#22c55e" : "#ef4444" }}>
                                                    {s.projected_win_rate}%
                                                </span>{" "}
                                                <span className="text-mute">(su {s.projected_sample_size} trade rimanenti)</span>
                                            </span>
                                            <span className="text-mute">
                                                Bucket: {s.bucket_size} trade · {s.strength}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <button
                                        onClick={() => handleAccept(s.id)}
                                        data-testid={`accept-${s.id}`}
                                        className="text-[10px] tracking-widest px-2 py-1 border border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
                                        ACCETTA
                                    </button>
                                    <button
                                        onClick={() => handleDismiss(s.id)}
                                        data-testid={`dismiss-${s.id}`}
                                        className="text-[10px] tracking-widest px-2 py-1 border border-soft text-mute hover:bg-elev2">
                                        SCARTA
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-[10px] text-mute italic">
                        Il layer di auto-correzione confronta i risultati dei trade automatici con la baseline e
                        propone regole solo quando ha almeno 30 trade nel bucket e la divergenza è ≥ 15 punti %.
                        Nessun cambio ai parametri viene applicato senza Accept manuale.
                    </div>
                )}
            </div>
            )}

            {/* Griglia card per ogni parametro */}
            <div
                data-testid="edge-bucket-grid"
                className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(buckets.groups || []).map((group) => (
                    <BucketGroupCard key={group.key} group={group} />
                ))}
            </div>
        </div>
    );
}

function BucketGroupCard({ group }) {
    const visible = (group.buckets || []).filter(b => b.total > 0);
    const isEmpty = visible.length === 0;
    return (
        <div
            data-testid={`bucket-group-${group.key}`}
            className="border border-soft bg-elev p-3">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-widest text-mute uppercase font-bold">
                    {group.label}
                </div>
                <div className="text-[9px] text-mute tnum">
                    {group.n_trades} trade
                </div>
            </div>
            {isEmpty ? (
                <div className="text-[10px] text-mute italic py-3">
                    Nessun dato disponibile per questo parametro.
                </div>
            ) : (
                <table className="w-full text-xs tnum">
                    <thead>
                        <tr className="text-[9px] tracking-widest text-mute uppercase border-b border-soft">
                            <th className="text-left py-1">Bucket</th>
                            <th className="text-right py-1" style={{ color: "#22c55e" }}>✓ Win</th>
                            <th className="text-right py-1" style={{ color: "#ef4444" }}>✗ Loss</th>
                            <th className="text-right py-1">WR%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(b => (
                            <tr
                                key={b.bucket}
                                data-testid={`bucket-row-${group.key}-${String(b.bucket).replace(/\s+/g, "-")}`}
                                className="border-b border-soft/40">
                                <td className="py-1.5 font-bold text-dim">{b.bucket}</td>
                                <td className="py-1.5 text-right" style={{ color: "#22c55e" }}>{b.winners}</td>
                                <td className="py-1.5 text-right" style={{ color: "#ef4444" }}>{b.losers}</td>
                                <td className="py-1.5 text-right"
                                    style={{ color: b.win_rate == null ? "var(--text-mute)" : b.win_rate >= 50 ? "#22c55e" : "#ef4444" }}>
                                    {b.win_rate == null ? "—" : `${b.win_rate}%`}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
